import { clsx } from 'clsx';
import * as React from 'react';

import ChevronLeftIcon from './assets/icon-chevron-left.svg';
import ChevronRightIcon from './assets/icon-chevron-right.svg';
import Actionable from './components/Actionable/Actionable';
import Image from './components/Image/Image';

import * as styles from './style.css';

interface CarouselProps {
  items?: readonly React.ReactNode[];
  itemToShow?: number;
  buttonOffsetTop?: number;
}

const LegacyCarousel: React.FC<CarouselProps> = ({
  items,
  itemToShow: initialItemsToShow = 3,
  buttonOffsetTop,
}) => {
  const carouselItemsRef = React.useRef<HTMLDivElement>(null);

  const [width, setWidth] = React.useState<number>(810 / initialItemsToShow);
  const [left, setLeft] = React.useState<number>(0);
  const [firstItemIndex, setFirstItemIndex] = React.useState<number>(0);
  const [itemToShow, setItemToShow] = React.useState<number>(3);

  const carouselItems = items || [];
  const itemCount = carouselItems.length;

  const setLeftPosition = (itemIndex: number) => {
    setFirstItemIndex(itemIndex);
    setLeft(-(width + 20) * itemIndex);
  };

  React.useEffect(() => {
    const actualItemToShow =
      carouselItems.length > initialItemsToShow
        ? initialItemsToShow
        : carouselItems.length;
    let carouselWidth = 810;
    if (carouselItemsRef && carouselItemsRef.current) {
      carouselWidth = carouselItemsRef.current.clientWidth;
    }

    const itemTotalWidth = carouselWidth - 20 * (actualItemToShow - 1);
    setWidth(itemTotalWidth / actualItemToShow);
    setItemToShow(actualItemToShow);
    setLeftPosition(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemToShow, carouselItems, itemCount]);

  const goToPrevItem = () => {
    const nextFirstIndex = firstItemIndex - itemToShow + 1;
    if (nextFirstIndex > 0) {
      setLeftPosition(nextFirstIndex);
    } else {
      setLeftPosition(0);
    }
  };

  const goToNextItem = () => {
    const lastItemIndex = firstItemIndex + itemToShow - 1;
    const nextLastIndex = lastItemIndex + itemToShow;
    if (nextLastIndex > carouselItems.length - 1) {
      setLeftPosition(carouselItems.length - itemToShow);
    } else {
      setLeftPosition(lastItemIndex);
    }
  };

  return (
    <div className={styles.carouselContainer}>
      {firstItemIndex !== 0 && (
        <Actionable
          role="button"
          ariaLabel="Previous"
          className={clsx(styles.slideButton, styles.alignLeft)}
          onClick={goToPrevItem}
          style={buttonOffsetTop ? { top: `${buttonOffsetTop - 22}px` } : {}}
        >
          <Image src={ChevronLeftIcon} alt="Previous" />
        </Actionable>
      )}
      <div className={styles.carouselItems} ref={carouselItemsRef}>
        <div
          className={styles.carousel}
          style={{ transform: `translateX(${left}px)` }}
        >
          {carouselItems.map((item, index) => (
            <div
              key={`carousel-${index}`}
              className={styles.item}
              style={{ width }}
            >
              {item}
            </div>
          ))}
        </div>
      </div>
      {firstItemIndex + itemToShow !== carouselItems.length && (
        <Actionable
          role="button"
          ariaLabel="Next"
          className={clsx(styles.slideButton, styles.alignRight)}
          onClick={goToNextItem}
          style={buttonOffsetTop ? { top: `${buttonOffsetTop - 22}px` } : {}}
        >
          <Image src={ChevronRightIcon} alt="Next" />
        </Actionable>
      )}
    </div>
  );
};

export default LegacyCarousel;
